"use client";
import React, { useEffect, useState, useMemo } from "react";
import Image from "next/image";
import { Col, Container, Row } from "react-bootstrap";
import { motion } from "framer-motion";
import odooLogo from "@/assets/images/logo/odoo.png";
import Button from "@/components/Button/Button";
import ContactFormModal from "@/components/ContactFormModal/ContactFormModal";
import "./BusinessModuleSection.scss";

const BusinessModuleSection = ({ module }) => {
  const [showModal, setShowModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const radius = useMemo(() => {
    if (windowWidth < 480) return 90;
    if (windowWidth < 768) return 120;
    if (windowWidth < 1200) return 160;
    return 200;
  }, [windowWidth]);

  if (!module) return null;
  const { title, description, modules } = module;

  const getPosition = (index, total) => {
    const angle = (index / total) * (2 * Math.PI) - Math.PI / 2;
    return {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
      rotate: (angle * 180) / Math.PI + 90,
    };
  };
  return (
    <section className="module-section">
      <Container>
        <Row className="align-items-center gy-5">
          <Col lg={5} md={12} className="order-1 order-lg-2">
            <div className="text-wrapper">
              <h2 className="module-title">{title}</h2>
              <p className="module-description">{description}</p>
              <div className="btn-wrapper">
                <Button
                  variant="primary"
                  size="lg"
                  className="talk-btn"
                  onClick={() => setShowModal(true)}
                  suppressHydrationWarning
                >
                  Talk With Us
                </Button>
              </div>
            </div>
          </Col>

          <Col
            lg={7}
            md={12}
            className="order-2 order-lg-1 d-flex justify-content-center"
          >
            <div className="circle-container">
              <div className="circle-wrapper">
                {modules.map((item, index) => {
                  const { x, y, rotate } = getPosition(index, modules.length);
                  return (
                    <motion.div
                      key={item.id || index}
                      className="module-item-wrapper"
                      initial={{ opacity: 0, scale: 0}}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        x: x,
                        y: y,
                      }}
                      transition={{
                        delay: index * 0.1,
                        type: "spring",
                        stiffness: 100,
                        damping: 15,
                      }}
                      style={{
                        position: "absolute",
                        // top: "50%",
                        // left: "50%",
                        rotate: `${rotate}deg`,
                      }}
                    >
                      <div className="arc-card">
                        {/* The Icon Badge */}
                        <div className="icon-badge">
                          <div style={{ transform: `rotate(${-rotate}deg)` }}>
                            <Image
                              src={item.icon}
                              alt={item.title}
                              width={24}
                              height={24}
                            />
                          </div>
                        </div>

                        {/* The Text Content */}
                        <div
                          className="content-upright"
                          style={{ transform: `rotate(${-rotate}deg)` }}
                        >
                          <p className="title">{item.title}</p>
                          <span className="desc">{item.description}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                <div className="center-logo">
                  <Image
                    src={odooLogo}
                    alt="odoo"
                    width={140}
                    height={60}
                    className="odoo-img"
                  />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <ContactFormModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={() => setShowModal(false)}
      />
    </section>
  );
};

export default BusinessModuleSection;
